import type {
  BodyBlock,
  Group,
  GroupKind,
  PreambleNode,
  Statute,
  Unit,
} from '../types';

/**
 * v2 Constitution adapter (Constitution of Pakistan 1973).
 *
 * Article-centric: leaves are emitted as Units with kind 'article' and
 * numberDisplay 'Article {n}'. Groups follow the Constitution's own
 * organisation (Part > Chapter > Article); chapters become Groups, with the
 * containing Part recorded in `Group.ancestors[]` so the bridge can render
 * Part / Chapter context above the article.
 *
 * Empty chapters (children: []) are skipped. Schedules are emitted as their
 * own Groups when present.
 */

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const trimOrEmpty = (v: unknown): string => asString(v).trim();

const CONTAINER_TYPES = new Set([
  'preamble',
  'preliminary',
  'part',
  'chapter',
  'heading',
  'schedule',
  'appendix',
  'order',
  'division',
]);

const LEAF_TYPES = new Set(['article', 'section']);

function devWarn(message: string, ...rest: unknown[]) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[v2ConstitutionAdapter] ${message}`, ...rest);
  }
}

function pickGroupKind(type: string): GroupKind {
  switch (type) {
    case 'preliminary':
      return 'preliminary';
    case 'part':
      return 'part';
    case 'chapter':
      return 'chapter';
    case 'order':
      return 'order';
    case 'schedule':
      return 'schedule';
    case 'appendix':
      return 'appendix';
    case 'division':
      return 'division';
    default:
      return 'other';
  }
}

function articleNumberFromNode(node: AnyRecord, fallback: string): string {
  const articleNo = trimOrEmpty(node.articleNo);
  if (articleNo) return articleNo;
  const label = trimOrEmpty(node.label);
  let m = label.match(/^Article\s+([A-Za-z0-9]+)$/i);
  if (m) return m[1];
  m = label.match(/([A-Za-z0-9]+)\s*$/);
  if (m) return m[1];
  const id = trimOrEmpty(node.id);
  const idMatch = id.match(/(?:^art-|-)([A-Za-z0-9]+)$/i);
  if (idMatch) return idMatch[1];
  return fallback;
}

function stripParenLabel(label: string): string {
  const t = label.trim();
  const m = t.match(/^\(([^)]+)\)$/);
  return m ? m[1] : t;
}

function mapClause(node: AnyRecord, idx: number): BodyBlock {
  const label = trimOrEmpty(node.label);
  const letter = stripParenLabel(label) || String(idx + 1);
  let text = trimOrEmpty(node.text);
  const subClauses = Array.isArray(node.subClauses) ? (node.subClauses as AnyRecord[]) : [];
  if (subClauses.length) {
    const tail = subClauses
      .map((sc) => trimOrEmpty(sc?.text))
      .filter(Boolean)
      .join('\n');
    if (tail) text = text ? `${text}\n${tail}` : tail;
  }
  return { kind: 'clause', letter, text };
}

function mapProviso(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = node.trim();
    return t ? { kind: 'proviso', text: t } : null;
  }
  if (isObject(node)) {
    const lab = trimOrEmpty(node.label);
    const text = trimOrEmpty(node.text);
    const merged = [lab, text].filter(Boolean).join(' ').trim();
    return merged ? { kind: 'proviso', text: merged } : null;
  }
  return null;
}

function mapExplanation(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = node.trim();
    return t ? { kind: 'explanation', text: t } : null;
  }
  if (isObject(node)) {
    const lab = trimOrEmpty(node.label);
    const text = trimOrEmpty(node.text);
    const merged = [lab, text].filter(Boolean).join(': ').trim();
    return merged ? { kind: 'explanation', text: merged } : null;
  }
  return null;
}

function mapSubsection(node: AnyRecord, idx: number): BodyBlock {
  const label = trimOrEmpty(node.label);
  const number = stripParenLabel(label) || String(idx + 1);
  const text = trimOrEmpty(node.text);

  const children: BodyBlock[] = [];
  const clauses = Array.isArray(node.clauses) ? (node.clauses as AnyRecord[]) : [];
  clauses.forEach((c, i) => children.push(mapClause(c, i)));
  const provisos = Array.isArray(node.provisos) ? node.provisos : [];
  provisos.forEach((p) => {
    const block = mapProviso(p);
    if (block) children.push(block);
  });
  const explanations = Array.isArray(node.explanations) ? node.explanations : [];
  explanations.forEach((e) => {
    const block = mapExplanation(e);
    if (block) children.push(block);
  });

  return {
    kind: 'subsection',
    number,
    text,
    ...(children.length ? { children } : {}),
  };
}

function mapArticleBody(node: AnyRecord): BodyBlock[] {
  const blocks: BodyBlock[] = [];
  const subsRaw = node.subsections ?? node.sub_sections ?? node.subSections;
  const subsections = Array.isArray(subsRaw) ? (subsRaw as AnyRecord[]) : [];

  if (subsections.length === 0) {
    const text = trimOrEmpty(node.fullText) || trimOrEmpty(node.text) || trimOrEmpty(node.content);
    if (text) blocks.push({ kind: 'paragraph', text });
  } else {
    subsections.forEach((sub, i) => blocks.push(mapSubsection(sub, i)));
  }

  const provisosTop = Array.isArray(node.provisos) ? node.provisos : [];
  provisosTop.forEach((p) => {
    const block = mapProviso(p);
    if (block) blocks.push(block);
  });
  const explanationsTop = Array.isArray(node.explanations) ? node.explanations : [];
  explanationsTop.forEach((e) => {
    const block = mapExplanation(e);
    if (block) blocks.push(block);
  });
  return blocks;
}

function makeArticleUnit(node: AnyRecord, fallbackOrder: number): Unit {
  const id = trimOrEmpty(node.id) || `art-${fallbackOrder}`;
  const labelRaw = trimOrEmpty(node.label);
  const titleRaw = trimOrEmpty(node.title) || labelRaw || `Article ${fallbackOrder}`;
  const number = articleNumberFromNode(node, String(fallbackOrder));
  const numberDisplay = labelRaw || `Article ${number}`;
  return {
    id,
    kind: 'article',
    number,
    numberDisplay,
    title: titleRaw,
    body: mapArticleBody(node),
  };
}

function structuralHeading(node: AnyRecord, fallback: string): string {
  const label = trimOrEmpty(node.label);
  const title = trimOrEmpty(node.title);
  if (label && title && label.toLowerCase() !== title.toLowerCase()) return `${label} ${title}`;
  if (label) return label;
  if (title) return title;
  return fallback;
}

function buildPreamble(node: AnyRecord): PreambleNode | undefined {
  const id = trimOrEmpty(node.id) || 'preamble';
  const titleField = trimOrEmpty(node.title) || trimOrEmpty(node.label) || 'Preamble';

  const body: BodyBlock[] = [];

  const directText = trimOrEmpty(node.text);
  if (directText) body.push({ kind: 'paragraph', text: directText });

  const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
  children.forEach((child) => {
    if (!isObject(child)) return;
    body.push(...mapArticleBody(child));
  });

  if (body.length === 0) {
    body.push({ kind: 'paragraph', text: titleField });
  }

  return { id, title: titleField, body };
}

function buildStatute(raw: AnyRecord, hint?: string): Statute {
  const metadata = isObject(raw.metadata) ? (raw.metadata as AnyRecord) : {};
  const title = trimOrEmpty(metadata.title) || 'Untitled Statute';
  const shortCode = trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.shortCode) || title;
  const yearRaw = metadata.year;
  const year = typeof yearRaw === 'number' && !Number.isNaN(yearRaw) ? yearRaw : 0;
  const jurisdiction = trimOrEmpty(metadata.jurisdiction) || 'PK';
  const category = hint || trimOrEmpty(metadata.category) || 'write-petition';

  const structure = Array.isArray(raw.structure) ? (raw.structure as AnyRecord[]) : [];
  const groups: Group[] = [];
  let preamble: PreambleNode | undefined;
  let groupSeq = 0;
  let unitSeq = 0;

  const collectArticles = (container: AnyRecord): Unit[] => {
    const out: Unit[] = [];
    const children = Array.isArray(container.children) ? (container.children as AnyRecord[]) : [];
    for (const child of children) {
      if (!isObject(child)) continue;
      if (LEAF_TYPES.has(trimOrEmpty(child.type))) out.push(makeArticleUnit(child, unitSeq++));
    }
    return out;
  };

  const visit = (node: AnyRecord, ancestors: string[]) => {
    if (!isObject(node)) return;
    const type = trimOrEmpty(node.type);

    if (type === 'preamble') {
      if (!preamble) preamble = buildPreamble(node);
      return;
    }

    if (LEAF_TYPES.has(type)) {
      const u = makeArticleUnit(node, unitSeq++);
      const sig = ancestors.join('|');
      const prev = groups[groups.length - 1];
      const prevSig = prev?.ancestors?.join('|') ?? '';
      if (prev && prev.kind === 'other' && prevSig === sig) {
        prev.units.push(u);
      } else {
        groups.push({
          id: `grp-${groupSeq++}`,
          kind: 'other',
          label: ancestors[ancestors.length - 1] || 'Articles',
          units: [u],
          ...(ancestors.length ? { ancestors: ancestors.slice(0, -1) } : {}),
        });
      }
      return;
    }

    if (CONTAINER_TYPES.has(type)) {
      const groupKind = pickGroupKind(type);
      const label = trimOrEmpty(node.label) || (type ? type[0].toUpperCase() + type.slice(1) : 'Group');
      const titleField = trimOrEmpty(node.title);
      const ownAncestors = ancestors.slice();

      const directArticles = collectArticles(node);
      if (directArticles.length > 0) {
        const id = trimOrEmpty(node.id) || `grp-${type}-${groupSeq++}`;
        groups.push({
          id,
          kind: groupKind,
          label,
          ...(titleField ? { title: titleField } : {}),
          ...(ownAncestors.length ? { ancestors: ownAncestors } : {}),
          units: directArticles,
        });
      }

      const trail = [...ancestors, structuralHeading(node, label)];
      const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
      children.forEach((c) => {
        if (!isObject(c)) return;
        if (CONTAINER_TYPES.has(trimOrEmpty(c.type))) visit(c, trail);
      });
      return;
    }

    if (type) {
      devWarn(`Unrecognized type "${type}" — descending into children`, node);
    }
    const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
    children.forEach((c) => visit(c as AnyRecord, ancestors));
  };

  structure.forEach((node) => visit(node, []));

  const idHint = hint || trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.title) || 'constitution';
  const id = idHint.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'constitution';

  return {
    id,
    title,
    shortCode,
    year,
    jurisdiction,
    category,
    ...(preamble ? { preamble } : {}),
    groups,
    metadata,
  };
}

export function adaptStatute(rawJson: unknown, hint?: string): Statute {
  if (!isObject(rawJson)) {
    devWarn('adaptStatute received non-object input', rawJson);
    return {
      id: 'unknown',
      title: '',
      shortCode: '',
      year: 0,
      jurisdiction: 'PK',
      category: hint || 'write-petition',
      groups: [],
      metadata: {},
    };
  }
  return buildStatute(rawJson, hint);
}
